import { useMemo, useState } from "react";
import { ChevronRight, Inbox, Loader2, RefreshCw } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Badge,
  PRIORITY_COLORS,
  STAGE_ORDER,
  STATUS_LABELS,
  TYPE_META,
  displayName,
  metaLine,
  statusColor,
  statusLabel,
} from "./shared";

const TYPE_FILTERS = [
  { key: "all", label: "Todos" },
  { key: "service", label: "Atendimentos" },
  { key: "agency", label: "Imobiliárias" },
  { key: "partner", label: "Parceiros" },
];

export const LeadsView = ({ leads, loading, onRefresh, onOpen, mode = "inbox" }) => {
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const isInbox = mode === "inbox";

  const filtered = useMemo(
    () =>
      leads.filter(
        (l) =>
          !(l.type !== "service" && l.status === "parceria_fechada") &&
          (typeFilter === "all" || l.type === typeFilter) &&
          (statusFilter === "all" || l.status === statusFilter)
      ),
    [leads, typeFilter, statusFilter]
  );

  const count = (fn) => leads.filter(fn).length;
  const statCards = isInbox
    ? [
        { label: "Leads na caixa de entrada", value: leads.length, testId: "admin-stat-total" },
        {
          label: "Atendimentos novos",
          value: count((l) => l.type === "service"),
          testId: "admin-stat-service",
        },
        {
          label: "Imobiliárias",
          value: count((l) => l.type === "agency"),
          testId: "admin-stat-agency",
        },
        {
          label: "Parceiros",
          value: count((l) => l.type === "partner"),
          testId: "admin-stat-partner",
        },
        {
          label: "Urgentes",
          value: count((l) => l.payload?.priority === "Urgente"),
          testId: "admin-stat-novo",
        },
      ]
    : [
        { label: "Solicitações", value: leads.length, testId: "admin-stat-total" },
        {
          label: "Aguardando orçamento",
          value: count((l) => l.status === "aguardando_orcamento"),
          testId: "admin-stat-orcamento",
        },
        {
          label: "Agendadas",
          value: count((l) => l.status === "agendado"),
          testId: "admin-stat-agendado",
        },
        {
          label: "Em execução",
          value: count((l) => l.status === "em_execucao"),
          testId: "admin-stat-execucao",
        },
        {
          label: "Concluídas",
          value: count((l) => l.status === "concluido"),
          testId: "admin-stat-concluido",
        },
      ];

  const statusOptions = isInbox
    ? Object.entries(STATUS_LABELS)
    : STAGE_ORDER.map((s) => [s, STATUS_LABELS[s]]);

  return (
    <div data-testid={isInbox ? "leads-inbox-view" : "solicitacoes-view"}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-petrol sm:text-3xl">
            {isInbox ? "Leads recebidos" : "Solicitações"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {isInbox
              ? "Solicitações novas aguardando qualificação. Classifique a demanda ou indique um prestador para movê-las às Solicitações."
              : "Chamados já qualificados, em atendimento ou concluídos."}
          </p>
        </div>
        <button
          onClick={onRefresh}
          data-testid="admin-refresh-button"
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-brand hover:text-brand"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="admin-stats">
        {statCards.map((s) => (
          <div
            key={s.testId}
            data-testid={s.testId}
            className="rounded-2xl border border-slate-200 bg-white p-5"
          >
            <p className="font-display text-3xl font-extrabold text-petrol">{s.value}</p>
            <p className="mt-1 text-xs font-medium text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        {isInbox && (
          <div className="flex flex-wrap rounded-full border border-slate-200 bg-white p-1">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setTypeFilter(f.key)}
                data-testid={`admin-filter-${f.key}`}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  typeFilter === f.key ? "bg-petrol text-white" : "text-slate-600 hover:text-petrol"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[210px] rounded-full bg-white" data-testid="admin-status-filter">
            <SelectValue placeholder="Etapa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as etapas</SelectItem>
            {statusOptions.map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 space-y-2.5">
        {loading && leads.length === 0 ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-7 w-7 animate-spin text-brand" />
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="rounded-2xl border border-dashed border-slate-300 bg-white/60 py-20 text-center"
            data-testid="admin-empty-state"
          >
            <Inbox className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">
              {isInbox
                ? "Nenhum lead novo por aqui. Os formulários do site caem direto nesta lista."
                : "Nenhuma solicitação qualificada ainda. Classifique um lead novo para ele aparecer aqui."}
            </p>
          </div>
        ) : (
          filtered.map((lead) => {
            const typeMeta = TYPE_META[lead.type] || TYPE_META.service;
            return (
              <button
                key={lead.id}
                onClick={() => onOpen(lead)}
                data-testid={`lead-row-${lead.id}`}
                className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-left transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-[0_16px_36px_-20px_rgba(11,25,44,0.3)]"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate font-display text-base font-bold text-petrol">
                      {displayName(lead)}
                    </h3>
                    <Badge className={typeMeta.classes}>{typeMeta.label}</Badge>
                    {lead.type === "service" && lead.payload?.priority && (
                      <Badge
                        className={PRIORITY_COLORS[lead.payload.priority] || "bg-slate-100 text-slate-500"}
                        testId={`lead-priority-${lead.id}`}
                      >
                        {lead.payload.priority}
                      </Badge>
                    )}
                    {lead.type === "service" && !lead.payload?.service_type && (
                      <Badge className="border border-dashed border-brand/50 bg-transparent text-brand">
                        Classificar
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 truncate font-mono text-[0.65rem] uppercase tracking-wider text-slate-400">
                    {metaLine(lead)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <Badge className={statusColor(lead.status)} testId={`lead-status-badge-${lead.id}`}>
                    {statusLabel(lead.status)}
                  </Badge>
                  <ChevronRight className="h-4 w-4 text-slate-300 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-brand" />
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
